import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { FC, useContext, useMemo } from "react";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { ExportGapFilterType } from "@app/exports/enumerations";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { UserContext } from "@app/user/UserContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { ReportCustomizationReducerActions } from "../interfaces/reportCustomizationReducerActionsEnum";
import { ReportType } from "../interfaces/reportTypeEnum";
import { getServiceAreaGapTypes } from "../helpers";
import { ReportCustomizationContext } from "../ReportCustomizationContext";
import { DropdownItem, MultiselectDropdown } from "@app/shared/checkbox-dropdown";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { Features } from "@app/feature-flags";

export const ServiceAreaSection: FC = () => {
  const rcContext = useContext(ReportCustomizationContext);
  const user = useContext(UserContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const isChangedGapsReport = !!rcContext.state.changedGapsType;
  const hasSpecialtySummaryReportAlignmentFlag = enabledFeatureFlags.has(Features.SPECIALTY_SUMMARY_REPORT_ALIGNMENT);
  const disableCurrentServiceAreaOptions = isChangedGapsReport || (!hasSpecialtySummaryReportAlignmentFlag && rcContext.state.includeOpportunity);
  const disableOpportunityOption = isChangedGapsReport || (!hasSpecialtySummaryReportAlignmentFlag && (rcContext.state.includeActive || rcContext.state.includePending || rcContext.state.includeApproved));

  const serviceAreaGapTypesOptions = useMemo(() => {
    const options = getServiceAreaGapTypes(user.roles);
    return options.map((o) => <option key={o.key} value={o.key}>{o.value}</option>);
  }, [user.roles]);

  const allCountiesSelected = useMemo(() => {
    const allCounties = rcContext.state.allServiceAreaCounties.flatMap((state) => state.children.map((child) => child.key));
    return allCounties.length === rcContext.state.selectedCounties.length && allCounties.every((county) => rcContext.state.selectedCounties.includes(county));
  }, [rcContext.state.selectedCounties, rcContext.state.allServiceAreaCounties]);

  const allStatesSelected = useMemo(() => {
    if (rcContext.state.reportType !== ReportType.StateSummary) {
      return false;
    }

    const allStates = rcContext.state.allServiceAreaStates.map((s) => s.key);
    return allStates.length === rcContext.state.selectedStates.length && allStates.every((stateKey) => rcContext.state.selectedStates.includes(stateKey));
  }, [rcContext.state.reportType, rcContext.state.selectedStates, rcContext.state.allServiceAreaStates]);

  const selectedStates: DropdownItem<string>[] = useMemo(() => {
    if (rcContext.state.reportType !== ReportType.StateSummary) {
      return [];
    }

    return rcContext.state.allServiceAreaStates.map((asa) => {
      if (rcContext.state.selectedStates.includes(asa.key)) {
        return { key: asa.key, name: asa.name };
      }
      return null;
    }).filter((a) => !!a);
  }, [rcContext.state.reportType, rcContext.state.selectedStates, rcContext.state.allServiceAreaStates]);

  const changeCurrentServiceAreaOption = (key: "includeActive" | "includePending" | "includeApproved", isChecked: boolean, action: ReportCustomizationReducerActions) => {
    const newNetworkAssessmentAnalysisTypes = [];
    const currentServiceAreaOptions = {
      includeActive: rcContext.state.includeActive,
      includeApproved: rcContext.state.includeApproved,
      includePending: rcContext.state.includePending,
      [key]: isChecked,
    };
    if (Object.values(currentServiceAreaOptions).some((v) => v)) {
      newNetworkAssessmentAnalysisTypes.push(NetworkAssessmentAnalysisType.ServiceArea);
    }
    if (rcContext.state.includeOpportunity) {
      newNetworkAssessmentAnalysisTypes.push(NetworkAssessmentAnalysisType.Opportunity);
    }

    rcContext.dispatch({
      payload: {
        [key]: isChecked,
        networkAssessmentAnalysisTypes: newNetworkAssessmentAnalysisTypes,
      },
      type: action,
    })
  }

  const changeOpportunityOption = (isChecked: boolean) => {
    const newNetworkAssessmentAnalysisTypes = [];
    const currentServiceAreaOptions = {
      includeActive: rcContext.state.includeActive,
      includeApproved: rcContext.state.includeApproved,
      includePending: rcContext.state.includePending,
    };
    if (Object.values(currentServiceAreaOptions).some((v) => v)) {
      newNetworkAssessmentAnalysisTypes.push(NetworkAssessmentAnalysisType.ServiceArea);
    }
    if (isChecked) {
      newNetworkAssessmentAnalysisTypes.push(NetworkAssessmentAnalysisType.Opportunity);
    }

    rcContext.dispatch({
      payload: {
        includeOpportunity: isChecked,
        networkAssessmentAnalysisTypes: newNetworkAssessmentAnalysisTypes,
      },
      type: ReportCustomizationReducerActions.ChangeIncludeOpportunity,
    })
  }

  return (
    <>
      <div className="row">
        <h2 className="header-s col-auto">Service Area</h2>
      </div>
      {rcContext.state.reportType !== ReportType.AdequacyAggregate && (
        <div className="row">
          {rcContext.state.reportType === ReportType.StateSummary ? (
            <MultiselectDropdown
              containerClassName="col-4"
              customSelectInputText={allStatesSelected ? "All Areas" : undefined}
              items={rcContext.state.allServiceAreaStates}
              label="Service Area"
              onSelectionChanged={(items) => rcContext.dispatch({ payload: { selectedStates: items.map((k) => k.key) }, type: ReportCustomizationReducerActions.ChangeSelectedStates })}
              selectedItems={selectedStates}
              disabled={rcContext.state.allServiceAreaStates.length === 0 || !!rcContext.state.changedGapsType}
              testId="report-customization__service-area--states-only"
            />
          ) : (
            <MultiselectNestedDropdown
              containerClassName="col-4"
              customSelectInputText={allCountiesSelected ? "All Areas" : undefined}
              items={rcContext.state.allServiceAreaCounties}
              label="Service Area"
              onSelectionChanged={(keys) => rcContext.dispatch({ payload: { selectedCounties: keys }, type: ReportCustomizationReducerActions.ChangeSelectedCounties })}
              selectedItems={rcContext.state.selectedCounties}
              disabled={rcContext.state.allServiceAreaCounties.length === 0 || !!rcContext.state.changedGapsType}
              testId="report-customization__service-area--states-and-counties"
            />
          )}

          <QESSelect
            containerClassName="col-4"
            label="Service Area Gaps"
            value={rcContext.state.serviceAreaGapsType}
            onChange={(e) => rcContext.dispatch({ payload: { serviceAreaGapsType: e.target.value as ExportGapFilterType }, type: ReportCustomizationReducerActions.ChangeServiceAreaGapsType })}
            disabled={isChangedGapsReport}
            testId="report-customization__export-gap-filter-type"
          >
            {serviceAreaGapTypesOptions}
          </QESSelect>
        </div>
      )}
      <QESCheckbox
        containerClassName="row ml-1"
        label="Active counties"
        checked={rcContext.state.includeActive}
        onChange={(e) => changeCurrentServiceAreaOption("includeActive", e.target.checked, ReportCustomizationReducerActions.ChangeIncludeActive)}
        disabled={disableCurrentServiceAreaOptions}
        testId="report-customization__include-active"
      />
      <QESCheckbox
        containerClassName="row ml-1"
        label="Pending counties"
        checked={rcContext.state.includePending}
        onChange={(e) => changeCurrentServiceAreaOption("includePending", e.target.checked, ReportCustomizationReducerActions.ChangeIncludePending)}
        disabled={disableCurrentServiceAreaOptions}
        testId="report-customization__include-pending"
      />
      <QESCheckbox
        containerClassName="row ml-1"
        label="Approved counties"
        checked={rcContext.state.includeApproved}
        onChange={(e) => changeCurrentServiceAreaOption("includeApproved", e.target.checked, ReportCustomizationReducerActions.ChangeIncludeApproved)}
        disabled={disableCurrentServiceAreaOptions}
        testId="report-customization__include-approved"
      />
      <QESCheckbox
        containerClassName="row ml-1"
        label="Opportunity counties"
        checked={rcContext.state.includeOpportunity}
        onChange={(e) => changeOpportunityOption(e.target.checked)}
        disabled={disableOpportunityOption}
        testId="report-customization__include-opportunity"
      />
    </>
  )
}